import { Button } from "@/design-system/Button/Button";
import { CelebrationDialog } from "@/design-system/CelebrationDialog/CelebrationDialog";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { CoinIcon } from "@/designSystemIcons";
import { useTranslation } from "@repo/shared/i18n";
import { StreamlineTotals } from "@repo/shared/streamline";
import { convertSBCPrice } from "@repo/shared/utils";

/**
 * Shown once every row is done. The rows have nothing left to say at that point,
 * so this floats over them with what the build cost and the one action left:
 * mark it complete, or continue once it already is, back to the SBC list.
 */
export const CompletionPanel = ({
  totals,
  onContinue,
  isMarkedComplete,
  isOpen,
  onClose,
}: {
  totals: StreamlineTotals;
  onContinue: () => void;
  isMarkedComplete: boolean;
  isOpen: boolean;
  /** The user can put it away and keep editing. */
  onClose: () => void;
}) => {
  const { t } = useTranslation("sbcs");
  return (
    <CelebrationDialog
      isOpen={isOpen}
      onClose={onClose}
      icon={<ItemScoreDiamond size={40} />}
      title={t("streamline.sbcComplete")}
      subtitle={t("streamline.pointsCompleted", {
        earned: totals.pointsEarned.toLocaleString(),
        target: totals.targetPoints.toLocaleString(),
      })}
      action={
        <Button variant="primary" size="medium" onClick={onContinue}>
          {isMarkedComplete
            ? t("common:continue")
            : t("streamline.markComplete")}
        </Button>
      }
    >
      <div className="flex items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">
            {t("streamline.spent")}
          </span>
          <span className="flex items-center gap-1.5">
            <CoinIcon size={14} />
            <span className="text-base font-extrabold text-white">
              {convertSBCPrice(totals.costSpent)}
            </span>
          </span>
        </div>
        <div className="h-[38px] w-px bg-streamline-current-border" />
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">
            {t("streamline.savedWithClub")}
          </span>
          <span className="flex items-center gap-1.5">
            <CoinIcon size={14} />
            <span className="text-base font-extrabold text-sbc-club">
              {convertSBCPrice(totals.clubSaving)}
            </span>
          </span>
        </div>
      </div>
    </CelebrationDialog>
  );
};
